import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toaster from '@amzn/meridian/toaster';

import { ProxyRelationshipsStep } from './ProxyWidgetSteps/ProxyRelationshipsStep';
import { AssignProxyStep } from './ProxyWidgetSteps/AssignProxyStep';
import { ShareTeamStep } from './ProxyWidgetSteps/ShareTeamStep';
import {
  getUsersBeingProxied,
  getModalTitle,
  localizeErrorMessage,
} from './ProxyWidgetSteps/utils';
import { ProxyStep } from './ProxyWidgetSteps/StepEnum';

import DataSource from '../../../api';
import { StateContext } from '../../../GlobalStateStore';
import { EmptyTeamProfile, Profile, TeamProfile } from '../../../Types';
import DataState from '../../../Types/DataState';
import { localizeErrorCode, ErrorCode } from '../../../Types/ErrorCode';
import {
  EMPTY_PROXY_RESPONSE,
  ProxyResponse,
} from '../../../Types/ProxyResponse';
import { getCurrentWeek } from '../../../Util';
import { EmptyTeamProxyResponse, GetProxy } from '../../../api/Proxy/schema';
import PerformancePortalContext from '../../PerformancePortalContext';
import Naws, { QueryVariables } from '../../../api/Naws';

interface OMProxyWidgetProps {
  userProfile: Profile;
}

const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');

const OMProxyWidget: React.FC<OMProxyWidgetProps> = ({ userProfile }) => {
  const intl = useIntl();
  const globalState = useContext(StateContext);
  const {
    state: { inspireOptions },
  } = globalState;
  const { refetchProfiles } = useContext(PerformancePortalContext);

  const userID = userProfile.user.userID;
  const { cscEntityPrefix } = inspireOptions.data;

  const [omStep, setOmStep] = useState<ProxyStep>(ProxyStep.RELATIONSHIPS);
  const [selfProxyStep, setSelfProxyStep] = useState(false);
  const [selectedTMUserId, setSelectedTMUserId] = useState<string>('');
  const [selectedProxyUsername, setSelectedProxyUserName] =
    useState<string>('');
  const [selfProxyUsername, setSelfProxyUsername] = useState<string>('');
  const [removeProxy, setRemoveProxy] =
    useState<ProxyResponse>(EMPTY_PROXY_RESPONSE);
  const [removeProxyState, setRemoveProxyState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.INITIALIZED);
  const [addProxy, setAddProxy] = useState<ProxyResponse>(EMPTY_PROXY_RESPONSE);
  const [addProxyState, setAddProxyState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.INITIALIZED);
  const [proxyResponseMessage, setProxyResponseMessage] = useState('');
  const [errorToasts, setErrorToasts] = useState<Array<{ id: string }>>([]);
  const [teamProfile, setTeamProfile] = useState<TeamProfile>(EmptyTeamProfile);
  const [teamProfileState, setTeamProfileState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.LOADING);
  const [teamProxy, setTeamProxy] = useState<GetProxy>(EmptyTeamProxyResponse);
  const [teamProxyState, setTeamProxyState] = useState<
    (typeof DataState)[keyof typeof DataState]
  >(DataState.LOADING);
  const [loadingProxyIds, setLoadingProxyIds] = useState<Set<string>>(
    new Set()
  );
  const [removedProxyInfo, setRemovedProxyInfo] = useState<{
    proxy: string;
    tm: string;
  } | null>(null);
  const [showAddToast, setShowAddToast] = useState(false);
  const [showRemoveToast, setShowRemoveToast] = useState(false);
  const [open, setOpen] = useState(false);

  const onClickButton = useCallback(() => {
    setSelectedTMUserId('');
    setSelectedProxyUserName('');
    setSelfProxyUsername('');
    setShowAddToast(false);
    setShowRemoveToast(false);
    setOmStep(ProxyStep.RELATIONSHIPS);
    setSelfProxyStep(false);
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
    setErrorToasts([]);
    setShowAddToast(false);
    setShowRemoveToast(false);
    setRemovedProxyInfo(null);
    setOmStep(ProxyStep.RELATIONSHIPS);
    setSelfProxyStep(false);
  }, []);

  const onRemoveProxy = (
    proxyUserID: string,
    tmUserID: string,
    uniqueId: string,
    proxyAlias: string,
    tmAlias: string,
    shouldRefetchProfiles: boolean = false
  ): void => {
    if (tmUserID) {
      setLoadingProxyIds(prev => new Set(prev).add(uniqueId));
      const removeProxyInput = apiNaws.prepareQueryForRemoveProxy(
        tmUserID,
        proxyUserID,
        cscEntityPrefix
      );

      apiNaws.removeProxy(
        removeProxyInput,
        response => {
          setRemoveProxy(response);
          setRemoveProxyState(DataState.LOADED);
          setLoadingProxyIds(new Set());
          if (response.success) {
            setRemovedProxyInfo({ proxy: proxyAlias, tm: tmAlias });
            if (shouldRefetchProfiles && refetchProfiles) {
              refetchProfiles();
            }
          }
        },
        setRemoveProxyState
      );
    }
  };

  const onAddProxy = (
    tmUserId: string = selectedTMUserId,
    proxyUsername: string = selectedProxyUsername
  ): void => {
    if (userID) {
      const addProxyInput = apiNaws.prepareQueryForAddProxy(
        tmUserId,
        proxyUsername,
        cscEntityPrefix,
        []
      );

      apiNaws.addProxy(addProxyInput, setAddProxy, setAddProxyState);
    }
  };

  const onRemoveSelfProxy = (proxyUserID: string): void => {
    const proxyCoach = userProfile.proxyCoaches.find(
      coach => coach.tmEntity.userID === proxyUserID
    );

    const proxyAlias = proxyCoach ? proxyCoach.tmEntity.userName : '';
    const tmAlias = userProfile.user.userName;

    onRemoveProxy(proxyUserID, userID, proxyUserID, proxyAlias, tmAlias, true);
  };

  useEffect(() => {
    if (removeProxyState === DataState.LOADED && removeProxy.success) {
      setLoadingProxyIds(new Set());

      const managerUserIds = teamProfile.reportingTeam
        .filter(member => member.isManager)
        .map(member => member.relatedUser.userID);

      const queryVariables = apiNaws.prepareQueryForGetTeamProxy(
        userID,
        managerUserIds,
        cscEntityPrefix
      );

      apiNaws.getTeamProxy(queryVariables, setTeamProxy, setTeamProxyState);
    }
  }, [removeProxyState, removeProxy.success]);

  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED && userID) {
      const profileQuery = apiNaws.prepareTeamProfileDataQuery(
        userID,
        cscEntityPrefix
      );

      apiNaws.getTeamProfileData(
        profileQuery,
        setTeamProfile,
        setTeamProfileState
      );
    }
  }, [inspireOptions.state, userID]);

  useEffect(() => {
    if (addProxyState === DataState.LOADED) {
      const { success, messageCode } = addProxy;
      if (!success) {
        setAddProxyState(DataState.ERROR);
        const localizedMessage = intl.formatMessage(
          localizeErrorCode(messageCode as ErrorCode),
          { alias: selectedProxyUsername ?? '' }
        );
        setProxyResponseMessage(localizedMessage);
        setErrorToasts([{ id: 'error-toast' }]);
      }
    }
  }, [addProxyState, addProxy]);

  useEffect(() => {
    if (removeProxyState === DataState.ERROR) {
      setLoadingProxyIds(new Set());
      setProxyResponseMessage('Unable to end proxy status. Please try again.');
      setErrorToasts([{ id: 'error-toast' }]);
    } else if (removeProxyState === DataState.LOADED) {
      const { success, messageCode } = removeProxy;
      if (!success) {
        setRemoveProxyState(DataState.ERROR);
        const localizedMessage = localizeErrorMessage(intl, messageCode, '');
        setProxyResponseMessage(localizedMessage);
        setErrorToasts([{ id: 'error-toast' }]);
        setLoadingProxyIds(new Set());
      }
    }
  }, [removeProxyState, removeProxy, intl]);

  useEffect(() => {
    if (inspireOptions.data.isOm && open) {
      const managerUserIds = teamProfile.reportingTeam
        .filter(member => member.isManager)
        .map(member => member.relatedUser.userID);

      const queryVariables = apiNaws.prepareQueryForGetTeamProxy(
        userID,
        managerUserIds,
        cscEntityPrefix
      );

      apiNaws.getTeamProxy(queryVariables, setTeamProxy, setTeamProxyState);
    }
  }, [open, userID, teamProfile, inspireOptions.data.isOm]);

  useEffect(() => {
    if (
      teamProfileState === DataState.LOADED &&
      addProxyState === DataState.LOADED &&
      addProxy.success
    ) {
      setOpen(false);
      setShowAddToast(true);
      if (refetchProfiles && selfProxyStep) {
        refetchProfiles();
      }
    }
  }, [
    teamProfileState,
    addProxyState,
    addProxy.success,
    refetchProfiles,
    selfProxyStep,
  ]);

  useEffect(() => {
    if (
      teamProfileState === DataState.LOADED &&
      removeProxyState === DataState.LOADED &&
      removeProxy.success
    ) {
      setOpen(false);
      setShowRemoveToast(true);
    }
  }, [teamProfileState, removeProxyState, removeProxy.success]);

  return (
    <Box>
      {showAddToast && (
        <Toaster
          toasts={[
            {
              id: selfProxyStep
                ? 'cs_perfportal_add_selfproxy_message'
                : 'cs_perfportal_om_add_tmproxy_message',
            },
          ]}
          onCloseToast={() => setShowAddToast(false)}
          alignmentHorizontal='center'
        >
          {(): React.ReactElement => (
            <Alert
              type='success'
              toast={true}
              onClose={() => setShowAddToast(false)}
            >
              <Text alignment='center'>
                {selfProxyStep ? (
                  <FormattedMessage
                    id='cs_perfportal_add_selfproxy_message'
                    defaultMessage="You've successfully shared your team with {alias} for coaching"
                    values={{ alias: selfProxyUsername }}
                  />
                ) : (
                  <FormattedMessage
                    id='cs_perfportal_om_add_tmproxy_message'
                    defaultMessage="You've successfully shared {tmUsername}'s team with {proxyTm} for coaching."
                    values={{
                      proxyTm: selectedProxyUsername,
                      tmUsername: teamProfile?.reportingTeam.find(
                        tm => tm.relatedUser.userID === selectedTMUserId
                      )?.relatedUser.userName,
                    }}
                  />
                )}
              </Text>
            </Alert>
          )}
        </Toaster>
      )}

      {showRemoveToast && (
        <Toaster
          toasts={[{ id: 'cs_perfportal_remove_proxy_success_om' }]}
          onCloseToast={() => setShowRemoveToast(false)}
          alignmentHorizontal='center'
        >
          {(): React.ReactElement => (
            <Alert
              type='success'
              toast={true}
              onClose={() => setShowRemoveToast(false)}
            >
              <Text alignment='center'>
                <FormattedMessage
                  id='cs_perfportal_remove_proxy_success_om'
                  defaultMessage='Proxy successfully ended. {proxy} is no longer proxy for {tm}.'
                  values={{
                    proxy: removedProxyInfo?.proxy,
                    tm: removedProxyInfo?.tm,
                  }}
                />
              </Text>
            </Alert>
          )}
        </Toaster>
      )}

      {proxyResponseMessage && (
        <Toaster
          toasts={errorToasts}
          onCloseToast={() => {
            setErrorToasts([]);
            setRemoveProxyState(DataState.INITIALIZED);
            setAddProxyState(DataState.INITIALIZED);
          }}
          alignmentHorizontal='center'
          alignmentVertical='top'
        >
          {(toast): React.ReactElement => (
            <Alert type='error' toast={true} onClose={toast.onClose}>
              {proxyResponseMessage}
            </Alert>
          )}
        </Toaster>
      )}

      <Column spacing='300'>
        <Row>
          <Text type='h100'>
            <FormattedMessage
              id='cs_perfportal_proxies'
              defaultMessage='Proxies'
            />
          </Text>
          <Text type='h100'>
            <Link
              onClick={onClickButton}
              type='secondary'
              data-testid='manage-proxy-button'
              data-csa-c-type='link'
              data-csa-c-slot-id='share-proxy-link-slot-id'
              data-csa-c-content-id='share-proxy-link'
            >
              <Icon tokens={exportSmallTokens} />{' '}
              <FormattedMessage
                id='cs_perfportal_manage'
                defaultMessage='Manage'
              />
            </Link>
          </Text>
        </Row>
        <Row>
          <Text>
            <FormattedMessage
              id='cs_perfportal_manage_proxies_description'
              defaultMessage='Manage proxies for yourself and the TMs you manage'
            />
          </Text>
        </Row>

        <Modal
          title={getModalTitle(intl, true, omStep, selfProxyStep)}
          open={open}
          onClose={onClose}
          scrollContainer='viewport'
          aria-describedby='modal-description'
          width={
            selfProxyStep ? 450 : omStep === ProxyStep.RELATIONSHIPS ? 600 : 450
          }
          bodySpacingInset='400 400 500'
        >
          {selfProxyStep ? (
            <ShareTeamStep
              proxyCoachCount={userProfile.proxyCoaches.length}
              proxyCoaches={userProfile.proxyCoaches}
              proxyUserName={selfProxyUsername}
              addProxyState={addProxyState}
              removeProxyState={removeProxyState}
              profileState={DataState.LOADED}
              setProxyUserName={setSelfProxyUsername}
              onRemoveProxy={onRemoveSelfProxy}
              onAddProxy={() => onAddProxy(userID, selfProxyUsername)}
              onClickFooterButton={onClose}
            />
          ) : omStep === ProxyStep.RELATIONSHIPS ? (
            <ProxyRelationshipsStep
              teamProxy={teamProxy}
              teamProxyState={teamProxyState}
              loadingProxyIds={loadingProxyIds}
              proxyResponseMessage={proxyResponseMessage}
              onRemoveOMProxy={onRemoveProxy}
              onRemoveProxy={onRemoveSelfProxy}
              onClose={onClose}
              setStep={setOmStep}
              setRemoveProxyState={setRemoveProxyState}
              teamProfile={teamProfile}
              userProfile={userProfile}
              cscEntityPrefix={cscEntityPrefix}
              onAssignProxyForSelf={() => setSelfProxyStep(true)}
            />
          ) : (
            <AssignProxyStep
              proxyResponseMessage={proxyResponseMessage}
              selectedTMUserId={selectedTMUserId}
              selectedProxyUsername={selectedProxyUsername}
              teamProfile={teamProfile}
              addProxyState={addProxyState}
              onAddProxy={onAddProxy}
              onClickFooterButton={() => {
                setOpen(false);
                setErrorToasts([]);
                setProxyResponseMessage('');
                setAddProxyState(DataState.INITIALIZED);
              }}
              setAddProxyState={setAddProxyState}
              setSelectedTMUserId={setSelectedTMUserId}
              setSelectedProxyUserName={setSelectedProxyUserName}
              getUsersBeingProxied={() => getUsersBeingProxied(teamProxy)}
              cscEntityPrefix={cscEntityPrefix}
            />
          )}
        </Modal>
      </Column>
    </Box>
  );
};

export default OMProxyWidget;
